import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AlertType } from './helpers';
import useStyles from '../../styles/alertForm';
import {
  LocationAlertParamAlertMethodsInner,
  EmailAlertMethod,
  WebhookAlertMethod,
  WebHookParams,
} from '../../services/api';

interface AlertMethodsFormProps {
  value: LocationAlertParamAlertMethodsInner[];
  onChange: (methods: LocationAlertParamAlertMethodsInner[]) => void;
  allowEditing: boolean;
}

function createEmptyWebhookMethod(): LocationAlertParamAlertMethodsInner {
  return {
    type: 'webhook',
    params: { url: '' } as WebHookParams,
  } as LocationAlertParamAlertMethodsInner;
}

function createEmptyEmailMethod(): LocationAlertParamAlertMethodsInner {
  return {
    type: 'email',
    params: {
      recipients: [],
      sender: '',
    },
  } as LocationAlertParamAlertMethodsInner;
}

function isEmailMethod(
  method: LocationAlertParamAlertMethodsInner
): method is { type: 'email' } & EmailAlertMethod {
  return method.type === 'email';
}

function isWebhookMethod(
  method: LocationAlertParamAlertMethodsInner
): method is { type: 'webhook' } & WebhookAlertMethod {
  return method.type === 'webhook';
}

// Turn a comma-separated string into a trimmed array of addresses.
function parseAddressList(input: string): string[] {
  return input
    .split(',')
    .map((address) => address.trim())
    .filter(Boolean);
}

function formatAddressList(addresses?: string[] | null): string {
  return addresses?.join(', ') ?? '';
}

function AlertMethodsFormSection({ value, onChange, allowEditing }: AlertMethodsFormProps) {
  const { classes } = useStyles();

  const addMethod = () => {
    onChange([...value, createEmptyWebhookMethod()]);
  };

  const removeMethod = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const updateMethodType = (index: number, newType: AlertType) => {
    const replacement =
      newType === AlertType.email ? createEmptyEmailMethod() : createEmptyWebhookMethod();

    onChange(value.map((method, i) => (i === index ? replacement : method)));
  };

  const updateEmailField = (index: number, patch: Partial<EmailAlertMethod['params']>) => {
    onChange(
      value.map((method, i) => {
        if (i !== index || !isEmailMethod(method)) {
          return method;
        }

        return {
          ...method,
          params: {
            ...method.params,
            ...patch,
          },
        };
      })
    );
  };

  const updateWebhookField = (index: number, patch: Partial<WebHookParams>) => {
    onChange(
      value.map((method, i) => {
        if (i !== index || !isWebhookMethod(method)) {
          return method;
        }

        return {
          ...method,
          params: {
            ...method.params,
            ...patch,
          },
        };
      })
    );
  };

  return (
    <Box className={classes.formContainer}>
      <InputLabel sx={{ mb: 2 }}>Select Alert methods</InputLabel>

      {value.map((method, index) => (
        <Stack
          // eslint-disable-next-line react/no-array-index-key
          key={`${method.type}-${index}`}
          direction="row"
          spacing={1.5}
          sx={{ my: 1.25 }}
          alignItems="flex-start"
        >
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>

            <Select
              label="Type"
              variant="standard"
              value={method.type}
              onChange={(event) => updateMethodType(index, event.target.value as AlertType)}
              disabled={!allowEditing}
            >
              {Object.values(AlertType).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isWebhookMethod(method) && (
            <TextField
              fullWidth
              variant="standard"
              size="small"
              label="URL"
              value={method.params.url}
              onChange={(event) =>
                updateWebhookField(index, {
                  url: event.target.value,
                })
              }
              placeholder="https://..."
              disabled={!allowEditing}
              error={method.params.url.length > 0 && method.params.url === 'https://'}
            />
          )}

          {isEmailMethod(method) && (
            <Stack direction="column" spacing={1} flexGrow={1}>
              <TextField
                fullWidth
                variant="standard"
                size="small"
                label="Recipients (comma-separated)"
                value={formatAddressList(method.params.recipients)}
                onChange={(event) =>
                  updateEmailField(index, {
                    recipients: parseAddressList(event.target.value),
                  })
                }
                placeholder="recipient1@example.com, recipient2@example.com"
                disabled={!allowEditing}
              />

              <TextField
                fullWidth
                variant="standard"
                size="small"
                label="CC addresses (optional - comma-separated)"
                value={formatAddressList(method?.params?.CcAddresses)}
                onChange={(event) =>
                  updateEmailField(index, {
                    CcAddresses: parseAddressList(event.target.value),
                  })
                }
                placeholder="cc@example.com, cc2@example.com"
                disabled={!allowEditing}
              />

              <TextField
                fullWidth
                variant="standard"
                size="small"
                label="BCC addresses (optional - comma-separated)"
                value={formatAddressList(method.params.BccAddresses)}
                onChange={(event) =>
                  updateEmailField(index, {
                    BccAddresses: parseAddressList(event.target.value),
                  })
                }
                placeholder="bcc@example.com, bcc2@example.com"
                disabled={!allowEditing}
              />
            </Stack>
          )}

          <IconButton
            size="small"
            onClick={() => removeMethod(index)}
            color="error"
            disabled={!allowEditing}
            sx={{ mt: 0.5 }}
            aria-label={`Remove ${method.type} alert method`}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}

      <Tooltip title={allowEditing ? 'Add Method' : ''}>
        <span>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon fontSize="small" color="success" />}
            onClick={addMethod}
            disabled={!allowEditing}
          >
            Add Method
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}

export default AlertMethodsFormSection;
